import styled, { css } from 'styled-components';
import { COLOR } from '@src/styles/color';
import {
  CLIP_HEIGHT,
  CLIP_WAVEFORM_HEIGHT,
  TRACK_HEIGHT,
  TRIM_HANDLE_HIT_PX,
} from '@src/constants/timeline';

export const StyledLane = styled.div`
  position: relative;
  width: 100%;
  height: ${TRACK_HEIGHT}px;
`;

// Time-range label shown at the centre of a clip while it's dragged / trimmed —
// same as the voice track's.
export const StyledDragTooltip = styled.span`
  position: absolute;
  top: -6px;
  left: ${({ left }) => left}px;
  transform: translateX(-50%);
  z-index: 3;
  padding: 2px 6px;
  border-radius: 4px;
  background-color: ${COLOR.darkBlue};
  color: ${COLOR.white};
  font-size: 10px;
  line-height: 14px;
  white-space: nowrap;
  pointer-events: none;
`;

// Holds the append button at the end of the track's content.
export const StyledAppendWrap = styled.div`
  position: absolute;
  top: 50%;
  left: ${({ left }) => left}px;
  transform: translateY(-50%);
  padding-right: 8px;
`;

// A music clip, positioned/sized by time (left/width set inline). Laid out as a
// column: name label on top, waveform band below (like the voice/media clips).
// The dragged clip sits above its neighbours; touch-action: none so a touch drag
// doesn't scroll the page.
export const StyledMusicClip = styled.button`
  box-sizing: border-box;
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  height: ${CLIP_HEIGHT}px;
  display: flex;
  flex-direction: column;
  justify-content: start;
  gap: 2px;
  padding: 4px 0;
  overflow: hidden;
  text-align: left;
  border: 0.5px solid
    ${({ selected }) => (selected ? COLOR.blueV2 : COLOR.bluev2[24])};
  border-radius: 8px;
  background-color: ${COLOR.bluev2[8]};
  touch-action: none;
  cursor: ${({ dragging, isLocked, isPlaying }) => {
    if (isLocked || isPlaying) return 'default';
    return dragging ? 'grabbing' : 'grab';
  }};
  z-index: ${({ dragging, selected }) => (dragging || selected ? 1 : 'auto')};
  /* Locked while a download/export runs — inert, but keeps its normal look. */
  ${({ isLocked }) =>
    isLocked &&
    css`
      pointer-events: none;
    `}
`;

export const StyledMusicLabel = styled.span`
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 0 4px;
  font-size: 10px;
  font-weight: 500;
  line-height: 16px;
  color: ${COLOR.blueV2};

  svg {
    flex: none;
    font-size: 12px;
  }

  span {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    color: ${COLOR.label2[64]};
  }
`;

// The waveform band under the label. overflow: hidden clips the fill's trimmed
// tail; the fill inside is pinned to the left.
export const StyledWaveformSlot = styled.span`
  position: relative;
  display: block;
  height: ${CLIP_WAVEFORM_HEIGHT}px;
  margin: 0 4px;
  overflow: hidden;
`;

// Box the Waveform fills; its width + left shift are set inline to the whole
// song's span, so the slot above clips the trimmed intro/tail.
export const StyledWaveformFill = styled.span`
  position: relative;
  display: block;
  height: 100%;
`;

// Grab zone for one trim edge on a selected clip — a full-height strip hugging
// that edge. Width capped at 50% so the left/right zones never overlap on a
// narrow clip.
export const StyledTrimHandle = styled.span`
  position: absolute;
  top: 0;
  bottom: 0;
  ${({ side }) => (side === 'right' ? 'right: 0;' : 'left: 0;')}
  width: min(${TRIM_HANDLE_HIT_PX}px, 50%);
  display: flex;
  align-items: center;
  justify-content: ${({ side }) =>
    side === 'right' ? 'flex-end' : 'flex-start'};
  padding: 0 1px;
  z-index: 2;
  cursor: ew-resize;
  touch-action: none;
`;

// The visible bar inside a grab zone; non-interactive so the whole zone (not
// just the 2px bar) takes the pointer.
export const StyledTrimBar = styled.span`
  width: 2px;
  height: 20px;
  border-radius: 100px;
  background-color: ${COLOR.blueV2};
  pointer-events: none;
`;
